/**
 * Shared design tokens for the Broker View tab.
 *
 * The Broker View scopes to ``broker_action_filter == 'INCLUDE'`` VINs; priority
 * is a code-computed severity tier (0 Critical .. 3 Low) derived from fixed-range
 * aging + engagement signals, the Tier filter is gone, and the map plots
 * *vehicles* (aggregated marker points) rather than dealers. Three vehicle-level
 * filter groups (Days Listed, Listing Occurrences, Total Bids) replace the old
 * Tier row.
 *
 * Keep palette + bucket-index alignment here so the map, chips, and cards stay
 * in sync with the backend bucket indices.
 */

import type { BrokerVehiclePoint, BrokerScorePart, CoreClientsMeta, CoreMetricsSummary, ListingStats, BrokerOfferMinTier } from '../../api/types';

// -----------------------------------------------------------------------------
// Admin-tunable calculation thresholds (shared Broker settings). The frontend
// mirrors the backend severity/price math for its client-side badges, so it must
// use the SAME thresholds. Rather than thread a config object through every
// component, we hold the active thresholds in module state and set them once when
// the dashboard loads /broker-view/settings (mirrors the backend's self-fetch).
// Cohort bands are NOT here — they're a fixed upstream contract.
// -----------------------------------------------------------------------------
export interface BrokerThresholds {
  urgency: {
    reprice_days: number;
    fresh_days: number;
    aged_days: number;
    bids_gt: number;
    listing_occ_gt: number;
    velocity_fast_days: number;
    velocity_slow_days: number;
    coverage_high_gt: number;
    coverage_low_lt: number;
    ignore_second_chance_bids: boolean;
    weights: {
      repriced: number;
      fresh: number;
      under_market: number;
      over_market: number;
      has_bids: number;
      relisted: number;
      aged: number;
      supply_low: number;
      supply_high: number;
      supply_excessive: number;
      demand_hot: number;
      demand_cold: number;
      velocity_fast: number;
      velocity_slow: number;
      coverage_high: number;
      coverage_low: number;
    };
    tier_cutoffs: { critical: number; high: number; medium: number };
  };
  price_bands: { under: number; right: number; high: number };
  offer_min_tiers: BrokerOfferMinTier[];
}

export const DEFAULT_BROKER_THRESHOLDS: BrokerThresholds = {
  urgency: {
    reprice_days: 10,
    fresh_days: 3,
    aged_days: 21,
    bids_gt: 0,
    listing_occ_gt: 1,
    velocity_fast_days: 30,
    velocity_slow_days: 60,
    // buyer_coverage counts the 730-day V5 buyer population, so these are in the
    // thousands. Must match DEFAULT_BROKER_SETTINGS.
    coverage_high_gt: 7500,
    coverage_low_lt: 1000,
    ignore_second_chance_bids: false,
    // Percentage scale: the boosts total 100, so a score IS the share of the
    // available urgency a vehicle earned. Must match DEFAULT_BROKER_SETTINGS in
    // broker_settings_service.py -- this file re-scores VINs client-side.
    weights: {
      repriced: 17, fresh: 12, under_market: 12, over_market: 0, has_bids: 6, relisted: 6, aged: -6,
      supply_low: 12, supply_high: -6, supply_excessive: -12, demand_hot: 12, demand_cold: -12,
      velocity_fast: 11, velocity_slow: -12, coverage_high: 12, coverage_low: -12,
    },
    tier_cutoffs: { critical: 65, high: 53, medium: 35 },
  },
  price_bands: { under: 0.90, right: 1.10, high: 1.25 },
  offer_min_tiers: [
    { min: 0, max: 4999, pct: 0.70 },
    { min: 5000, max: 9999, pct: 0.75 },
    { min: 10000, max: 14999, pct: 0.90 },
    { min: 15000, max: 19999, pct: 0.91 },
    { min: 20000, max: 24999, pct: 0.92 },
    { min: 25000, max: 29999, pct: 0.92 },
    { min: 30000, max: 34999, pct: 0.93 },
    { min: 35000, max: 39999, pct: 0.94 },
    { min: 40000, max: 44999, pct: 0.94 },
    { min: 45000, max: 49999, pct: 0.94 },
    { min: 50000, max: null, pct: 0.94 },
  ],
};

let ACTIVE_THRESHOLDS: BrokerThresholds = DEFAULT_BROKER_THRESHOLDS;

// Anything deriving urgency / price / bucket labels from module state has to be
// told when an admin saves new settings, or the board keeps scoring against the
// config it loaded at mount.
const thresholdListeners = new Set<() => void>();

/** Subscribe to shared-settings changes. Returns an unsubscribe function. */
export function onBrokerThresholdsChange(fn: () => void): () => void {
  thresholdListeners.add(fn);
  return () => thresholdListeners.delete(fn);
}

/** Shape of the raw settings payload the setter accepts (thresholds + buckets). */
interface BrokerSettingsLike {
  urgency?: Partial<BrokerThresholds['urgency']>;
  price_bands?: Partial<BrokerThresholds['price_bands']>;
  offer_min_tiers?: BrokerOfferMinTier[];
  filters?: {
    days_listed?: BrokerFilterBucket[];
    listing_occ?: BrokerFilterBucket[];
    total_bids?: BrokerFilterBucket[];
    reprice?: BrokerFilterBucket[];
    vdp_views?: BrokerFilterBucket[];
    workbook_saves?: BrokerFilterBucket[];
  };
}

/** Overlay the shared Broker settings' threshold groups + custom filter buckets
 * onto module state. Missing groups fall back to defaults; extra keys (e.g.
 * `fit`) are ignored. Notifies subscribers so an open board re-scores. */
export function setBrokerThresholds(cfg: BrokerSettingsLike | null | undefined): void {
  if (!cfg) {
    ACTIVE_THRESHOLDS = DEFAULT_BROKER_THRESHOLDS;
    ACTIVE_CUSTOM_FILTERS = DEFAULT_CUSTOM_FILTERS;
    thresholdListeners.forEach((fn) => fn());
    return;
  }
  ACTIVE_THRESHOLDS = {
    urgency: {
      ...DEFAULT_BROKER_THRESHOLDS.urgency,
      ...(cfg.urgency ?? {}),
      weights: { ...DEFAULT_BROKER_THRESHOLDS.urgency.weights, ...(cfg.urgency?.weights ?? {}) },
      tier_cutoffs: { ...DEFAULT_BROKER_THRESHOLDS.urgency.tier_cutoffs, ...(cfg.urgency?.tier_cutoffs ?? {}) },
    },
    price_bands: { ...DEFAULT_BROKER_THRESHOLDS.price_bands, ...(cfg.price_bands ?? {}) },
    offer_min_tiers: cfg.offer_min_tiers ?? DEFAULT_BROKER_THRESHOLDS.offer_min_tiers,
  };
  const f = cfg.filters;
  ACTIVE_CUSTOM_FILTERS = {
    days_listed: (f?.days_listed?.length ? f.days_listed : DEFAULT_CUSTOM_FILTERS.days_listed),
    listing_occ: (f?.listing_occ?.length ? f.listing_occ : DEFAULT_CUSTOM_FILTERS.listing_occ),
    total_bids: (f?.total_bids?.length ? f.total_bids : DEFAULT_CUSTOM_FILTERS.total_bids),
    reprice: (f?.reprice?.length ? f.reprice : DEFAULT_CUSTOM_FILTERS.reprice),
    vdp_views: (f?.vdp_views?.length ? f.vdp_views : DEFAULT_CUSTOM_FILTERS.vdp_views),
    workbook_saves: (f?.workbook_saves?.length ? f.workbook_saves : DEFAULT_CUSTOM_FILTERS.workbook_saves),
  };
  thresholdListeners.forEach((fn) => fn());
}

/** The currently-active Broker thresholds (defaults until settings load). */
export function getBrokerThresholds(): BrokerThresholds {
  return ACTIVE_THRESHOLDS;
}


// -----------------------------------------------------------------------------
// Priority = severity tier — ordered highest → lowest urgency.
// Index alignment MUST mirror backend BROKER_SEVERITY_LABELS / the
// broker_severity_bucket_sql CASE: 0 Critical, 1 High, 2 Medium, 3 Low.
// -----------------------------------------------------------------------------

export type PriorityKey =
  | 'severity_critical'
  | 'severity_high'
  | 'severity_medium'
  | 'severity_low';

export const PRIORITY_ORDER: PriorityKey[] = [
  'severity_critical',
  'severity_high',
  'severity_medium',
  'severity_low',
];

export const PRIORITY_LABEL: Record<PriorityKey, string> = {
  severity_critical: 'Critical',
  severity_high: 'High',
  severity_medium: 'Medium',
  severity_low: 'Low',
};

/** Short label for the compact carousel-card priority badges. */
export const PRIORITY_SHORT_LABEL: Record<PriorityKey, string> = {
  severity_critical: 'Critical',
  severity_high: 'High',
  severity_medium: 'Medium',
  severity_low: 'Low',
};

/**
 * Hover help explaining what each urgency tier means. Urgency is a computed
 * SIGNED score (§14.14): repriced / fresh (<3d) / at-or-under-market and the
 * engagement qualifiers push it up, while AGING pushes it down — so stalled,
 * unchanged inventory sinks and freshly-actionable units rise.
 */
export const PRIORITY_HELP: Record<PriorityKey, string> = {
  severity_critical:
    'Critical — highest urgency. Newly repriced and/or fresh, at or under market, often with buyer engagement. The best units to work right now.',
  severity_high:
    'High — strong opportunity signals (recently repriced, fresh, or at/under market with some engagement). Prioritize soon.',
  severity_medium:
    'Medium — a signal or two building (e.g. at/under market, or some engagement) but not yet compelling.',
  severity_low:
    'Low — stalled / aged with little action, or priced above market. Aging penalizes the score, so these sink to the bottom.',
};

// Colorblind-friendlier palette that reads on light + dark backgrounds.
export const PRIORITY_COLOR: Record<PriorityKey, { bg: string; fg: string; pin: string }> = {
  severity_critical: { bg: '#fee2e2', fg: '#b91c1c', pin: '#dc2626' }, // red (most urgent)
  severity_high:     { bg: '#fef3c7', fg: '#a16207', pin: '#d97706' }, // amber
  severity_medium:   { bg: '#dbeafe', fg: '#1d4ed8', pin: '#2563eb' }, // blue
  severity_low:      { bg: '#dcfce7', fg: '#15803d', pin: '#16a34a' }, // green (least urgent)
};

/** Priority bucket index (matches BrokerVehiclePoint.priority_bucket severity tier). */
export const PRIORITY_INDEX: Record<PriorityKey, number> = {
  severity_critical: 0,
  severity_high: 1,
  severity_medium: 2,
  severity_low: 3,
};

/**
 * Resolve a severity tier index back to its key. The backend emits tiers 0..3;
 * any out-of-range index defensively clamps to the last (lowest) tier.
 */
export function priorityKeyFromIndex(index: number): PriorityKey {
  return PRIORITY_ORDER[index] ?? PRIORITY_ORDER[PRIORITY_ORDER.length - 1];
}

// -----------------------------------------------------------------------------
// Buyer-match tier tokens (per buyer↔VIN match tightness). Shared so the VIN
// lists, pop-out, and buyer cards render the same Exact/Cohort/Similar/M-M tags.
// Trim + drivetrain are folded into "Exact" (tier 4) — never shown separately.
// -----------------------------------------------------------------------------
export const MATCH_TIER_LABEL: Record<number, string> = { 4: 'Exact', 3: 'Cohort', 2: 'Similar', 1: 'M/M', 0: 'Segment' };
export const MATCH_TIER_COLOR: Record<number, { bg: string; fg: string }> = {
  4: { bg: '#dcfce7', fg: '#15803d' },
  3: { bg: '#dbeafe', fg: '#1d4ed8' },
  2: { bg: '#fef3c7', fg: '#a16207' },
  1: { bg: '#f1f5f9', fg: '#475569' },
  0: { bg: '#ede9fe', fg: '#6d28d9' },
};
export const MATCH_TIER_DESC: Record<number, string> = {
  4: 'Exact: same year / mileage / CR cohort + trim + drivetrain',
  3: 'Cohort: same year / mileage / CR band',
  2: 'Similar: same age + mileage or CR band',
  1: 'Make/model only (outside the year/mileage/CR cohort)',
  0: 'Segment: same body/size segment — a different make/model in the same class (incl. same-parent siblings, e.g. GMC ↔ Chevy)',
};

// -----------------------------------------------------------------------------
// Per-VIN severity — computed client-side from the same fixed-range signals the
// backend uses (broker_severity_score_sql). Lets the VIN table show + sort by an
// urgency tier without a per-row backend field.
// -----------------------------------------------------------------------------

export interface VinSeveritySignals {
  list_days?: number | null;
  days_since_first_offering?: number | null;
  listing_occurences?: number | null;
  total_bids?: number | null;
  days_since_reprice?: number | null;
  // Pricing inputs for the price-position factor. Field names cover both shapes
  // that call these helpers: the VIN-table row (``adj_mmr``) and the full
  // vehicle detail (``adjusted_mmr``).
  buy_now_price?: number | null;
  floor_price?: number | null;
  // §14.4 seller's true price point (lowest counter) — lowers the effective asking.
  lowest_seller_counter?: number | null;
  // §S2 opening bid — the fourth asking-price input. Null until VIN_DETAILS ships it.
  starting_bid_price?: number | null;
  allow_make_offer?: number | null;
  adj_mmr?: number | null;
  adjusted_mmr?: number | null;
  base_mmr?: number | null;
  // §14.14 VMA demand signals (from STG_VMA_MARKET_INSIGHTS via VIN_DETAILS).
  wholesale_supply_level?: string | null;
  wholesale_demand_level?: string | null;
  wholesale_avg_days_to_sell?: number | null;
  // §14.14 buyer coverage — distinct buyers active in this VIN's cohort.
  buyer_coverage?: number | null;
  // §14.2 2nd-chance relist flag (suppresses the has-bids point when the admin toggle is on).
  is_second_chance?: number | null;
}

/**
 * Lowest buyer entry across posted prices and the configured make-offer minimum.
 * Sub-$100 posted prices are ignored. Mirrors ``broker_price_ratio_sql``.
 */
export function vinAskingPrice(v: VinSeveritySignals): number | null {
  const g = (x: number | null | undefined): number | null =>
    x != null && Number.isFinite(x) && x >= BROKER_MIN_VALID_PRICE ? x : null;
  const candidates = [
    g(v.buy_now_price), g(v.floor_price),
    g(v.lowest_seller_counter), g(v.starting_bid_price),
  ].filter((x): x is number => x != null);
  if (v.allow_make_offer === 1) {
    const basis = g(v.floor_price) ?? g(v.buy_now_price);
    if (basis != null) {
      const tier = ACTIVE_THRESHOLDS.offer_min_tiers.find(
        t => basis >= t.min && (t.max == null || basis <= t.max),
      );
      const openingBid = g(v.starting_bid_price);
      if (tier) {
        const entry = basis * tier.pct;
        if (openingBid == null || entry < openingBid) candidates.push(entry);
      }
    }
  }
  return candidates.length ? Math.min(...candidates) : null;
}

function vinOfferingAge(v: VinSeveritySignals): number | null {
  // Only legacy payloads without the field use relisting age. A known null must
  // stay unknown, just as it does in the backend's first-offering expression.
  return v.days_since_first_offering === undefined ? v.list_days ?? null : v.days_since_first_offering;
}

/**
 * Asking-price / market-value (MMR) ratio, or ``null`` when either side is
 * missing. Market = adjusted/adj MMR (else base MMR). Mirrors the backend
 * ``broker_price_ratio_sql``.
 */
export function vinPriceRatio(v: VinSeveritySignals): number | null {
  const asking = vinAskingPrice(v);
  const market = v.adjusted_mmr ?? v.adj_mmr ?? v.base_mmr ?? null;
  if (asking == null || market == null || market === 0) return null;
  return asking / market;
}

/**
 * Price-position bucket index from a ratio. ``0`` Under (<0.90), ``1`` Right
 * (0.90-1.10), ``2`` High (1.10-1.25), ``3`` Over (>1.25); ``-1`` unknown.
 * Mirrors the backend ``broker_price_bucket_sql``.
 */
export function vinPriceBucketIndex(ratio: number | null): number {
  if (ratio == null) return -1;
  const { under, right, high } = ACTIVE_THRESHOLDS.price_bands;
  if (ratio < under) return 0;
  if (ratio <= right) return 1;
  if (ratio <= high) return 2;
  return 3;
}

// -----------------------------------------------------------------------------
// Data-quality formatters (§8.3). Auction feeds carry sentinel/bad values —
// negative or out-of-range CR grades, a 999,999 "unknown" mileage — that must
// render as "unknown" (–) rather than as a real number.
// -----------------------------------------------------------------------------

/** A CR grade is only meaningful in 0–5; treat out-of-range (incl. negative bad
 * data, e.g. VIN 1FTEW1CP1LKD74239) as unknown. */
export function isValidCrGrade(cr: number | null | undefined): cr is number {
  return cr != null && Number.isFinite(cr) && cr >= 0 && cr <= 5;
}

/** CR grade for display: out-of-range / null → "–", else fixed to ``digits``. */
export function fmtCrGrade(cr: number | null | undefined, digits = 1): string {
  return isValidCrGrade(cr) ? Number(cr).toFixed(digits) : '–';
}

/** Mileage for display: null / non-finite / the 999,999 sentinel → "–". */
export function fmtMileage(mi: number | null | undefined): string {
  if (mi == null || !Number.isFinite(mi) || mi >= 999999) return '–';
  return Math.round(mi).toLocaleString();
}

interface VinBundleFields {
  mileage?: number | null;
  cr_grade?: number | null;
  adj_mmr?: number | null;
  site_location?: string | null;
  auction?: string | null;
  vehicle_city?: string | null;
  vehicle_state?: string | null;
  vehicle_zip?: string | null;
  vehicle_miles_from_seller?: number | null;
}

/**
 * BRK-5.1 the facts a broker groups a seller's units by, beside Year/Make/Model:
 * miles, CR, adjusted MMR. Missing values are named ("miles n/a") rather than
 * dropped, so an absent odometer never reads as a short list (BRK-3.2).
 */
export function vinBundleFacts(v: VinBundleFields): string {
  const miles = fmtMileage(v.mileage);
  const mmr = v.adj_mmr != null && Number.isFinite(v.adj_mmr) && v.adj_mmr > 0
    ? `MMR $${Math.round(v.adj_mmr).toLocaleString()}` : 'MMR n/a';
  return [
    miles === '–' ? 'miles n/a' : `${miles} mi`,
    isValidCrGrade(v.cr_grade) ? `CR ${fmtCrGrade(v.cr_grade)}` : 'CR n/a',
    mmr,
  ].join(' · ');
}

/**
 * BRK-5.3 where the unit physically is: the auction for onsite units, the pickup
 * city for offsite ones, with the distance when it is not at the seller. Null when
 * the listing carries no position.
 */
export function vinLocationLabel(v: VinBundleFields): { text: string; title: string } | null {
  const place = [v.vehicle_city, [v.vehicle_state, v.vehicle_zip].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const onsite = (v.site_location ?? '').trim().toUpperCase() === 'ONSITE';
  if (onsite && v.auction) {
    return {
      text: `At ${v.auction}`,
      title: `Onsite at ${v.auction}${place ? ` (${place})` : ''}`,
    };
  }
  if (!place) return null;
  const away = v.vehicle_miles_from_seller;
  return {
    text: away != null ? `${place} · ${away.toLocaleString()} mi from seller` : place,
    title: away != null
      ? `Offsite pickup location, ${away.toLocaleString()} miles from the seller's address`
      : 'Offsite pickup location (at or near the seller)',
  };
}

const VALUATION_SOURCE_LABEL: Record<string, string> = {
  MANHEIM_VAL_API: 'Manheim valuation',
  VAUTO_VAL_API: 'vAuto valuation (Manheim had none)',
  LISTING_FEED: 'Listing feed',
};

/**
 * BRK-1.2 / D-06 where a valuation came from and when it was last valued, with a
 * plain caution that Manheim.com may show a newer value. Age is reported, not
 * judged: D-06 settled on "show the date and say it may be out of date".
 */
export function fmtValuationSource(
  value: number | null | undefined,
  source: string | null | undefined,
  asOf: string | null | undefined,
  now: Date = new Date(),
): string {
  if (value == null) return '–';
  if (!source) return 'Source not recorded';
  const label = VALUATION_SOURCE_LABEL[source] ?? source;
  if (source === 'LISTING_FEED') return `${label} (no valuation date)`;
  const day = asOf ? asOf.slice(0, 10) : '';
  const t = Date.parse(`${day}T00:00:00`);
  if (!day || Number.isNaN(t)) return `${label} · date not recorded`;
  const today = Date.parse(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T00:00:00`);
  const days = Math.round((today - t) / 86_400_000);
  const age = days <= 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
  return `${label} · last valued ${day} (${age}) · may differ from current Manheim.com`;
}

/** Minimum credible vehicle asking price. Below this ($1 placeholders, etc.) is a
 * junk sentinel that must not drive the price position (§8.3). Mirrors the backend
 * ``BROKER_MIN_VALID_PRICE``. */
export const BROKER_MIN_VALID_PRICE = 100;

/** §14.14 inverted, signed severity score (higher = more urgent). Mirrors the backend. */
export function vinSeverityScore(v: VinSeveritySignals): number {
  const ld = vinOfferingAge(v);
  const lo = v.listing_occurences ?? 0;
  const tb = v.total_bids ?? 0;
  const dsr = v.days_since_reprice;
  const ratio = vinPriceRatio(v);
  const u = ACTIVE_THRESHOLDS.urgency;
  const w = u.weights;
  const atUnder = ACTIVE_THRESHOLDS.price_bands.right;
  let s = 0;
  s += dsr != null && dsr <= u.reprice_days ? w.repriced : 0;
  s += ld != null && ld < u.fresh_days ? w.fresh : 0;
  s += ratio != null && ratio <= atUnder ? w.under_market : 0;
  s += ratio != null && ratio > ACTIVE_THRESHOLDS.price_bands.high ? w.over_market : 0;
  // §14.2: a 2nd-chance relist's bids aren't clean intent — suppress when the toggle is on.
  s += tb > u.bids_gt && !(u.ignore_second_chance_bids && Number(v.is_second_chance) === 1) ? w.has_bids : 0;
  s += lo > u.listing_occ_gt ? w.relisted : 0;
  s += ld != null && ld >= u.aged_days ? w.aged : 0;
  s += vmaSupplyPoints(v.wholesale_supply_level, w);
  s += vmaDemandPoints(v.wholesale_demand_level, w);
  s += vmaVelocityPoints(v.wholesale_avg_days_to_sell, u, w);
  s += vmaCoveragePoints(v.buyer_coverage, u, w);
  return s;
}

/** VMA supply-level → points (Low + / High − / Excessive −; else 0). */
function vmaSupplyPoints(level: string | null | undefined, w: BrokerThresholds['urgency']['weights']): number {
  switch ((level ?? '').trim().toUpperCase()) {
    case 'LOW': return w.supply_low;
    case 'HIGH': return w.supply_high;
    case 'EXCESSIVE': return w.supply_excessive;
    default: return 0;
  }
}

/** VMA demand-level → points (Hot + / Cold −; Warm/else 0). */
function vmaDemandPoints(level: string | null | undefined, w: BrokerThresholds['urgency']['weights']): number {
  switch ((level ?? '').trim().toUpperCase()) {
    case 'HOT': return w.demand_hot;
    case 'COLD': return w.demand_cold;
    default: return 0;
  }
}

/** VMA velocity (days-to-sell) → points (fast + / slow −; else 0). */
function vmaVelocityPoints(days: number | null | undefined, u: BrokerThresholds['urgency'], w: BrokerThresholds['urgency']['weights']): number {
  if (days == null) return 0;
  if (days <= u.velocity_fast_days) return w.velocity_fast;
  if (days >= u.velocity_slow_days) return w.velocity_slow;
  return 0;
}

/** §14.14 buyer coverage → points (deep pool + / thin market −; NULL = 0). */
function vmaCoveragePoints(cov: number | null | undefined, u: BrokerThresholds['urgency'], w: BrokerThresholds['urgency']['weights']): number {
  if (cov == null) return 0;
  if (cov > u.coverage_high_gt) return w.coverage_high;
  if (cov > 0 && cov < u.coverage_low_lt) return w.coverage_low;
  return 0;
}

/** Severity tier key (0 Critical .. 3 Low) from the score. Mirrors the backend. */
export function vinSeverityKey(score: number): PriorityKey {
  const { critical, high, medium } = ACTIVE_THRESHOLDS.urgency.tier_cutoffs;
  if (score >= critical) return 'severity_critical';
  if (score >= high) return 'severity_high';
  if (score >= medium) return 'severity_medium';
  return 'severity_low';
}

/** Max possible (positive-only) severity score. The weights are percentages that
 * total 100, so this is normally exactly that; it is still summed rather than
 * assumed, because an admin can set weights by hand. */
export function vinSeverityMax(): number {
  const w = ACTIVE_THRESHOLDS.urgency.weights;
  return Object.values(w).reduce((sum, x) => sum + (x > 0 ? x : 0), 0);
}

/** The score as a share of the urgency a vehicle could earn. "46%" is checkable
 * against a cutoff; the raw "22" it replaced meant nothing without the maximum. */
export function vinSeverityPercent(score: number): number {
  const max = vinSeverityMax();
  return max > 0 ? Math.round((score / max) * 100) : 0;
}

/** One driver of a VIN's urgency score: the signal, its value, and its points. */
export interface VinSeverityPart {
  label: string;
  value: number;
  /** Human-readable value override (defaults to the numeric ``value``). */
  display?: string;
  points: number;
  detail: string;
}

/**
 * The per-signal breakdown behind a VIN's urgency score — so the UI can show
 * exactly which data points drive the tier. Mirrors ``vinSeverityScore``.
 */
export function vinSeverityParts(v: VinSeveritySignals): VinSeverityPart[] {
  const ld = vinOfferingAge(v);
  const lo = v.listing_occurences ?? 0;
  const tb = v.total_bids ?? 0;
  const dsr = v.days_since_reprice;
  const ratio = vinPriceRatio(v);
  const u = ACTIVE_THRESHOLDS.urgency;
  const w = u.weights;
  const atUnder = ACTIVE_THRESHOLDS.price_bands.right;
  return [
    { label: 'Newly repriced', value: dsr ?? 0, display: dsr == null ? '—' : `${dsr}d ago`, points: dsr != null && dsr <= u.reprice_days ? w.repriced : 0, detail: `Repriced ≤${u.reprice_days}d ago → ${w.repriced >= 0 ? '+' : ''}${w.repriced} — the price was just moved, best time to act` },
    { label: 'Fresh', value: ld ?? 0, display: ld == null ? '—' : `${ld}d`, points: ld != null && ld < u.fresh_days ? w.fresh : 0, detail: `On offer <${u.fresh_days}d in this sell cycle → ${w.fresh >= 0 ? '+' : ''}${w.fresh} — work it before it ages` },
    { label: 'Price vs market', value: ratio ?? 0, display: ratio == null ? '—' : `${Math.round(ratio * 100)}%`, points: ratio != null && ratio <= atUnder ? w.under_market : 0, detail: `At/under market (≤${Math.round(atUnder * 100)}% MMR) → ${w.under_market >= 0 ? '+' : ''}${w.under_market} — priced to move, more buyers viable` },
    { label: 'Over market', value: ratio ?? 0, display: ratio == null ? '—' : `${Math.round(ratio * 100)}%`, points: ratio != null && ratio > ACTIVE_THRESHOLDS.price_bands.high ? w.over_market : 0, detail: `Over market (>${Math.round(ACTIVE_THRESHOLDS.price_bands.high * 100)}% MMR) → ${w.over_market >= 0 ? '+' : ''}${w.over_market} — ${w.over_market === 0 ? 'off by default' : w.over_market > 0 ? 'stuck, needs a broker nudge' : 'unlikely to move, deprioritized'}` },
    { label: 'Current bids', value: tb, points: tb > u.bids_gt && !(u.ignore_second_chance_bids && Number(v.is_second_chance) === 1) ? w.has_bids : 0, detail: u.ignore_second_chance_bids && Number(v.is_second_chance) === 1 ? `2nd-chance relist → 0 (bids ignored)` : `any → ${w.has_bids >= 0 ? '+' : ''}${w.has_bids}` },
    { label: 'Relisted', value: lo, points: lo > u.listing_occ_gt ? w.relisted : 0, detail: `>${u.listing_occ_gt} listings → ${w.relisted >= 0 ? '+' : ''}${w.relisted}` },
    { label: 'Aged', value: ld ?? 0, display: ld == null ? '—' : `${ld}d`, points: ld != null && ld >= u.aged_days ? w.aged : 0, detail: `On offer ≥${u.aged_days}d in this sell cycle → ${w.aged} — stalled, likely other problems` },
    { label: 'Market supply', value: 0, display: v.wholesale_supply_level ?? '—', points: vmaSupplyPoints(v.wholesale_supply_level, w), detail: `Low → +${w.supply_low} · High → ${w.supply_high} · Excessive → ${w.supply_excessive}` },
    { label: 'Market demand', value: 0, display: v.wholesale_demand_level ?? '—', points: vmaDemandPoints(v.wholesale_demand_level, w), detail: `Hot → +${w.demand_hot} · Cold → ${w.demand_cold}` },
    { label: 'Velocity', value: v.wholesale_avg_days_to_sell ?? 0, display: v.wholesale_avg_days_to_sell == null ? '—' : `${v.wholesale_avg_days_to_sell}d to sell`, points: vmaVelocityPoints(v.wholesale_avg_days_to_sell, u, w), detail: `≤${u.velocity_fast_days}d → +${w.velocity_fast} · ≥${u.velocity_slow_days}d → ${w.velocity_slow}` },
    { label: 'Buyer coverage', value: v.buyer_coverage ?? 0, display: v.buyer_coverage == null ? '—' : `${v.buyer_coverage} buyers`, points: vmaCoveragePoints(v.buyer_coverage, u, w), detail: `>${u.coverage_high_gt} → +${w.coverage_high} · <${u.coverage_low_lt} → ${w.coverage_low}` },
  ];
}

/** Multiline tooltip spelling out a VIN's urgency tier + what drives it. */
export function vinSeverityTooltip(v: VinSeveritySignals): string {
  const score = vinSeverityScore(v);
  const tier = PRIORITY_LABEL[vinSeverityKey(score)];
  const lines = vinSeverityParts(v).map((p) => `• ${p.label}: ${p.display ?? p.value} → +${p.points}`);
  return `Urgency: ${tier} (${vinSeverityPercent(score)}% of the urgency a vehicle can earn)\n${lines.join('\n')}`;
}

/**
 * Tooltip spelling out a buyer's fit score and how each weighted component
 * contributed — the buyer-match parallel to ``vinSeverityTooltip``.
 */
export function fitScoreTooltip(score: number, matchLabel: string, parts: BrokerScorePart[]): string {
  const head = `Fit ${Math.round(score)}/100 · Match: ${matchLabel}`;
  // One decimal, and the ceiling where there is one: rounding each part to a
  // whole number made the list visibly fail to add up to the Fit on ~48% of
  // buyers, which is exactly what made the score look arbitrary.
  const lines = (parts || []).map((p) => {
    const pts = p.points.toFixed(1);
    return p.max_points != null
      ? `• ${p.label} (${p.detail}): ${pts} of ${p.max_points}`
      : `• ${p.label} (${p.detail}): ${p.points >= 0 ? '+' : ''}${pts}`;
  });
  return lines.length ? `${head}\n${lines.join('\n')}` : head;
}

/** The six weighted components vs. the read-time adjustments layered on top.
 *
 * Each part is rounded to one decimal for display, then the largest component
 * absorbs the leftover so the numbers on screen add up to the Fit EXACTLY.
 * Rounding parts independently left a visible mismatch (74.8 shown against a
 * Fit of 75), which is what made the score look made-up. The adjustment is
 * always under a tenth of a point on a ~20-point component.
 */
export function splitScoreParts(parts: BrokerScorePart[], score: number) {
  const all = (parts || []).map((p) => ({ ...p, display: Math.round(p.points * 10) / 10 }));
  if (all.length) {
    const residual = Math.round((score - all.reduce((s, p) => s + p.display, 0)) * 10) / 10;
    if (residual !== 0) {
      let biggest = 0;
      all.forEach((p, i) => { if (Math.abs(p.display) > Math.abs(all[biggest].display)) biggest = i; });
      all[biggest].display = Math.round((all[biggest].display + residual) * 10) / 10;
    }
  }
  return {
    components: all.filter((p) => p.max_points != null),
    adjustments: all.filter((p) => p.max_points == null),
    total: score,
  };
}

// -----------------------------------------------------------------------------
// Vehicle-level filter groups. Each group is an independent multi-select chip
// row: OR within a group, AND across groups. Bucket indices mirror the backend
// CASE expressions in get_broker_vehicle_marker_rows.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Custom vehicle-filter buckets (Days Listed / Listings / Bids). Fully
// admin-defined: an ordered list of {label, min, max} buckets per group. Held in
// module state + set from settings, mirroring ACTIVE_THRESHOLDS. Selections are
// bucket INDICES (the marker payload carries the matching index per VIN).
// -----------------------------------------------------------------------------
export interface BrokerFilterBucket { label: string; min: number | null; max: number | null }
export type CustomFilterGroup = 'days_listed' | 'listing_occ' | 'total_bids' | 'reprice' | 'vdp_views' | 'workbook_saves';

export const DEFAULT_CUSTOM_FILTERS: Record<CustomFilterGroup, BrokerFilterBucket[]> = {
  days_listed: [{ label: '0–3 Days', min: 0, max: 3 }, { label: '4+ Days', min: 4, max: null }],
  listing_occ: [{ label: '0–3 Listings', min: 0, max: 3 }, { label: '4+ Listings', min: 4, max: null }],
  total_bids: [{ label: 'No Bids', min: 0, max: 0 }, { label: 'Has Bids', min: 1, max: null }],
  reprice: [{ label: 'Repriced ≤10d', min: 0, max: 10 }, { label: 'Repriced 11+d', min: 11, max: null }],
  vdp_views: [{ label: 'No Views', min: 0, max: 0 }, { label: '1–9 Views', min: 1, max: 9 }, { label: '10+ Views', min: 10, max: null }],
  workbook_saves: [{ label: 'No Saves', min: 0, max: 0 }, { label: 'Saved', min: 1, max: null }],
};

let ACTIVE_CUSTOM_FILTERS: Record<CustomFilterGroup, BrokerFilterBucket[]> = DEFAULT_CUSTOM_FILTERS;

/** The active custom buckets for a filter group (defaults until settings load). */
export function getCustomBuckets(group: CustomFilterGroup): BrokerFilterBucket[] {
  return ACTIVE_CUSTOM_FILTERS[group] ?? DEFAULT_CUSTOM_FILTERS[group];
}

// Palette cycled through custom buckets by index (chip dot / legend color).
const CUSTOM_BUCKET_PALETTE = ['#0891b2', '#7c3aed', '#16a34a', '#d97706', '#dc2626', '#2563eb', '#db2777', '#0d9488'];
export function customBucketColor(index: number): string {
  return CUSTOM_BUCKET_PALETTE[((index % CUSTOM_BUCKET_PALETTE.length) + CUSTOM_BUCKET_PALETTE.length) % CUSTOM_BUCKET_PALETTE.length];
}

/** Label for a bucket index in a group ('' when out of range / unknown). */
export function customBucketLabel(group: CustomFilterGroup, index: number): string {
  return getCustomBuckets(group)[index]?.label ?? '';
}

export type PriceBucketKey = 'price_under' | 'price_right' | 'price_high' | 'price_over';
export const PRICE_ORDER: PriceBucketKey[] = ['price_under', 'price_right', 'price_high', 'price_over'];
export const PRICE_LABEL: Record<PriceBucketKey, string> = {
  price_under: 'Under Market',
  price_right: 'Priced Right',
  price_high: 'Above Market',
  price_over: 'Over Market',
};
/** Short label for compact badges (VIN table / marker popup). */
export const PRICE_SHORT_LABEL: Record<PriceBucketKey, string> = {
  price_under: 'Under',
  price_right: 'Right',
  price_high: 'High',
  price_over: 'Over',
};
/** Price bucket index (matches BrokerVehiclePoint.price_bucket; -1 = unknown). */
export const PRICE_INDEX: Record<PriceBucketKey, number> = {
  price_under: 0,
  price_right: 1,
  price_high: 2,
  price_over: 3,
};
export const PRICE_COLOR: Record<PriceBucketKey, { bg: string; fg: string }> = {
  price_under: { bg: '#dcfce7', fg: '#15803d' }, // green — aggressive/below market
  price_right: { bg: '#dbeafe', fg: '#1d4ed8' }, // blue — at market
  price_high:  { bg: '#fef3c7', fg: '#a16207' }, // amber — above market
  price_over:  { bg: '#fee2e2', fg: '#b91c1c' }, // red — well over market
};
export const PRICE_HELP: Record<PriceBucketKey, string> = {
  price_under: 'Asking under 90% of MMR — priced aggressively below market.',
  price_right: 'Asking 90–110% of MMR — priced at market. Stalled here means price isn’t the blocker; find a buyer.',
  price_high: 'Asking 110–125% of MMR — above market. A price move may help.',
  price_over: 'Asking over 125% of MMR — well above market; likely why it’s stalling.',
};

/**
 * Resolve a price bucket index back to its key. Unknown (-1) or out-of-range
 * returns ``null`` (no price data → no badge / no filter match).
 */
export function priceKeyFromIndex(index: number): PriceBucketKey | null {
  return PRICE_ORDER[index] ?? null;
}

/** Current selection across all Broker filter groups. Days/Listings/Bids are
 * bucket INDICES into the active custom buckets; priorities/price stay keyed. */
export interface BrokerFilterState {
  priorities: PriorityKey[];
  daysListed: number[];
  listingOcc: number[];
  totalBids: number[];
  reprice: number[];
  price: PriceBucketKey[];
  vdpViews: number[];
  workbookSaves: number[];
  /** §14.2 observed-engagement tokens: 'bidders' (bucket 2) / 'engaged' (bucket >= 1). */
  engaged: string[];
}

export const emptyBrokerFilterState = (): BrokerFilterState => ({
  priorities: [],
  daysListed: [],
  listingOcc: [],
  totalBids: [],
  reprice: [],
  price: [],
  vdpViews: [],
  workbookSaves: [],
  engaged: [],
});

export const brokerFilterCount = (f: BrokerFilterState): number =>
  f.priorities.length + f.daysListed.length + f.listingOcc.length + f.totalBids.length + f.reprice.length + f.price.length + f.vdpViews.length + f.workbookSaves.length + f.engaged.length;

/**
 * Whether a vehicle marker passes the current chip selection. Each group ORs its
 * own selected buckets; groups AND together. An empty group imposes no filter.
 */
export function vehicleMatchesFilters(p: BrokerVehiclePoint, f: BrokerFilterState): boolean {
  if (f.priorities.length > 0 && !f.priorities.some((k) => PRIORITY_INDEX[k] === p.priority_bucket)) {
    return false;
  }
  if (f.daysListed.length > 0 && !f.daysListed.includes(p.days_bucket)) {
    return false;
  }
  if (f.listingOcc.length > 0 && !f.listingOcc.includes(p.listing_bucket)) {
    return false;
  }
  if (f.totalBids.length > 0 && !f.totalBids.includes(p.bids_bucket)) {
    return false;
  }
  if (f.reprice.length > 0 && !f.reprice.includes(p.reprice_bucket)) {
    return false;
  }
  if (f.vdpViews.length > 0 && !f.vdpViews.includes(p.vdp_bucket ?? -1)) {
    return false;
  }
  if (f.workbookSaves.length > 0 && !f.workbookSaves.includes(p.workbook_bucket ?? -1)) {
    return false;
  }
  // price_bucket === -1 (unknown) matches no selected price key → filtered out.
  if (f.price.length > 0 && !f.price.some((k) => PRICE_INDEX[k] === p.price_bucket)) {
    return false;
  }
  // §14.2 observed engagement: 'bidders' needs bucket 2; 'engaged' needs bucket >= 1.
  if (f.engaged.length > 0) {
    const eb = p.engaged_bucket ?? 0;
    const ok = f.engaged.some((t) => (t === 'bidders' ? eb >= 2 : eb >= 1));
    if (!ok) return false;
  }
  return true;
}

// -----------------------------------------------------------------------------
// Per-dealer priority counts (derived from the vehicle markers).
// -----------------------------------------------------------------------------

export type BrokerPriorityCounts = Record<PriorityKey, number>;

export const emptyBrokerPriorityCounts = (): BrokerPriorityCounts => ({
  severity_critical: 0,
  severity_high: 0,
  severity_medium: 0,
  severity_low: 0,
});

/**
 * Lexicographic comparator ordering dealers by their priority-bucket vehicle
 * counts. With ``activePriorities`` non-empty the selected buckets order first
 * (in selection order); otherwise PRIORITY_ORDER applies. Final tie-break on
 * seller_name ASC.
 */
export function compareDealersByPriorityCounts(
  aCounts: BrokerPriorityCounts,
  bCounts: BrokerPriorityCounts,
  aName: string,
  bName: string,
  activePriorities: PriorityKey[],
): number {
  const keys = activePriorities.length > 0 ? activePriorities : PRIORITY_ORDER;
  for (const k of keys) {
    const av = aCounts[k] || 0;
    const bv = bCounts[k] || 0;
    if (av !== bv) return bv - av;
  }
  return (aName || '').localeCompare(bName || '');
}

// -----------------------------------------------------------------------------
// Tier — retained only for the dealer-detail pop-out badge (classifyTier reads
// the dealer's client_status). Tier is no longer a Broker map/filter dimension.
// -----------------------------------------------------------------------------

export type TierKey =
  | 'core_client'
  | 'at_risk'
  | 'emerging'
  | 'low_mmi_activity'
  | 'non_mmi_activity'
  | 'inactive';

export const TIER_LABEL: Record<TierKey, string> = {
  core_client: 'Core Client',
  at_risk: 'At Risk',
  emerging: 'Emerging',
  low_mmi_activity: 'Low MMI Activity',
  non_mmi_activity: 'Non-MMI Activity',
  inactive: 'Inactive',
};

export const TIER_COLOR: Record<TierKey, string> = {
  core_client: '#7c3aed',
  at_risk: '#ea580c',
  emerging: '#0891b2',
  low_mmi_activity: '#a16207',
  non_mmi_activity: '#65a30d',
  inactive: '#94a3b8',
};

/**
 * Classify a dealer into a tier using its canonical ``client_status``. Anything
 * unrecognized (incl. null/undefined) falls back to "inactive".
 */
export function classifyTier(core: CoreClientsMeta | undefined | null): TierKey {
  if (!core) return 'inactive';
  const status = (core.client_status || '').trim().toLowerCase();
  switch (status) {
    case 'core client': return 'core_client';
    case 'at risk': return 'at_risk';
    case 'emerging': return 'emerging';
    case 'low mmi activity': return 'low_mmi_activity';
    case 'non-mmi activity': return 'non_mmi_activity';
    case 'inactive': return 'inactive';
    default: return 'inactive';
  }
}

// -----------------------------------------------------------------------------
// Derived dealer view-model rendered by the carousel. Built by grouping the
// filtered vehicle markers per seller, then merged with the KPI card body.
// -----------------------------------------------------------------------------

export interface BrokerDealer {
  seller_id: string;
  salesforce_id: string | null;
  seller_name: string;
  total_vehicles: number;
  priority_counts: BrokerPriorityCounts;
  core: CoreClientsMeta;
  core_metrics: CoreMetricsSummary;
  listings_month: ListingStats;
  listings_month_prior: ListingStats;
  last_contact_date: string | null;
  days_since_last_contact: number | null;
  sfdc_contact_count_30d: number;
}

/** §6.2 the team's word for a Manheim buying account number. Every buyer SIFT
 *  shows is one (§3.5 excludes non-5mil ids), so the label is always accurate. */
export const BUYER_5MIL_HELP =
  '5mil — the Manheim buying account number. Searchable by this number or by name.';
